import { call, put, takeLatest } from 'redux-saga/effects'
import { InternalFetchMe } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { InternalIdentity } from 'src/interfaces/internalAuth'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { captureSentryError } from 'src/utils/sentry'
import { callSecureApi } from './api/callApi'

/**
 * No status means the request never reached the API — usually no access token yet, which is a
 * different problem from anything the API could tell us about.
 */
const buildFailureMessage = (
  apiMessage: string | undefined,
  status: number | undefined,
  error: unknown,
): string => {
  if (typeof apiMessage === 'string') {
    return apiMessage
  }

  if (status) {
    return `Internal access check failed (HTTP ${status})`
  }

  return `Could not reach the API: ${(error as Error)?.message ?? 'unknown error'}`
}

export function* onInternalFetchMe(action: AsyncAction<void>) {
  try {
    const { payload }: { payload: InternalIdentity } = yield call(
      callSecureApi,
      Endpoints.InternalAuthMe,
      { method: 'GET' },
    )

    yield put(InternalFetchMe.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    // A 403 here is the expected first-login outcome, not a fault: the user is authenticated but
    // has no internal role yet, so the message from the API is what should reach the screen.
    const apiMessage = (error as { payload?: { message?: string } })?.payload?.message
    const status = (error as { status?: number })?.status

    const message = buildFailureMessage(apiMessage, status, error)

    // Sentry gets the original throwable; the reducer only ever stores the display-safe string.
    captureSentryError(error, {
      message: '[saga.internalAuth.onInternalFetchMe] error',
      data: { status, apiMessage },
    })

    yield put(InternalFetchMe.failed(message))

    if (action.next) {
      action.next(message)
    }
  }
}

export function* internalAuthSaga() {
  yield takeLatest(InternalFetchMe.type.REQUEST, onInternalFetchMe)
}
